import './voice-input.less';
import voiceInputTemplate from './voice-input.tpl.html';
import { appConfig } from '../../../../shared/app/constants/app-config';

export const voiceInputComponentModule = angular
  .module('Q.components.voice-input', [])
  .factory(
    'RecorderService',
    function ($window, $q, $log, $timeout, $interval) {
      return {
        /**
         * Creates and initializes a new audio recorder
         * @returns {Promise} Promise that resolves with a recorder object or rejects with an error
         */
        createRecorder: function () {
          const deferred = $q.defer();

          // Check if the browser supports MediaRecorder API
          if (!($window.MediaRecorder && navigator.mediaDevices)) {
            deferred.reject('MediaRecorder not supported');
            return deferred.promise;
          }

          // Define supported audio formats in order of preference
          // We try these formats in sequence until we find one the browser supports
          const mimeTypes = [
            'audio/webm;codecs=opus',
            'audio/ogg;codecs=opus',
            'audio/webm',
            'audio/ogg',
          ];

          // Find the first supported audio format
          const supportedType = mimeTypes.find((type) => {
            try {
              return MediaRecorder.isTypeSupported(type);
            } catch (e) {
              $log.warn('Error checking mime type:', type, e);
              return false;
            }
          });

          // If no supported format is found, reject the promise
          if (!supportedType) {
            deferred.reject('No supported audio format found');
            return deferred.promise;
          }

          $log.info('Using audio format:', supportedType);

          // Declare audioMonitor at a higher scope so it's accessible in both .then() and .catch()
          let audioMonitor = null;

          // Request access to the user's microphone
          navigator.mediaDevices
            .getUserMedia({
              audio: {
                channelCount: 1, // Mono audio (single channel)
                echoCancellation: true, // Reduce echo
                noiseSuppression: true, // Reduce background noise
                sampleRate: 44100, // Standard audio sample rate
                autoGainControl: true, // Automatically adjust volume
                latency: 0, // Minimize delay
                volume: 1.0, // Full volume
              },
            })
            .then((stream) => {
              $log.log(
                'Got media stream:',
                stream.getAudioTracks()[0].getSettings(),
              );

              // Ensure the audio track is active
              const audioTrack = stream.getAudioTracks()[0];
              if (!audioTrack.enabled) {
                audioTrack.enabled = true;
              }

              // Create audio context for analyzing audio levels
              const audioContext = new ($window.AudioContext ||
                $window.webkitAudioContext)();

              // Ensure the AudioContext is running (might be suspended on some browsers)
              if (audioContext.state === 'suspended') {
                audioContext.resume();
              }

              // Set up audio analyzer to monitor audio levels
              const source = audioContext.createMediaStreamSource(stream);
              const analyser = audioContext.createAnalyser();
              analyser.fftSize = 2048; // Size of FFT (determines frequency resolution)
              analyser.smoothingTimeConstant = 0.8; // Smoothing factor for visualization
              source.connect(analyser);

              const dataArray = new Uint8Array(analyser.frequencyBinCount);

              // Create the recorder first before referencing it in checkAudio
              const recorder = new $window.MediaRecorder(stream, {
                mimeType: supportedType,
                audioBitsPerSecond: 128000, // 128kbps audio quality
              });

              // Verify if the stream is active and receiving data
              const tracks = stream.getAudioTracks();
              tracks.forEach((track) => {
                $log.log('Audio track state:', {
                  id: track.id,
                  label: track.label,
                  enabled: track.enabled,
                  muted: track.muted,
                  readyState: track.readyState,
                });
              });

              $log.log('Recorder state:', recorder.state);
              $log.log('Recorder mime type:', recorder.mimeType);

              /**
               * Function to monitor audio levels during recording
               * Helps detect if microphone is working properly
               */
              const checkAudio = () => {
                if (recorder && recorder.state === 'recording') {
                  // Get current audio frequency data
                  analyser.getByteFrequencyData(dataArray);

                  // Calculate average audio level
                  const audioLevel =
                    dataArray.reduce((a, b) => a + b) / dataArray.length;
                  $log.log('Audio level:', audioLevel);

                  // If no audio is detected after 500ms, show warnings
                  if (audioLevel === 0 && recorder.state === 'recording') {
                    $log.warn('No audio detected. Please verify that:');
                    $log.warn('1. The microphone is not muted');
                    $log.warn(
                      '2. The browser has permission to use the microphone',
                    );
                    $log.warn('3. The correct audio input device is selected');
                  }
                }
              };

              // Enumerate available audio input devices for debugging
              navigator.mediaDevices
                .enumerateDevices()
                .then((devices) => {
                  const audioDevices = devices.filter(
                    (device) => device.kind === 'audioinput',
                  );
                  $log.log('Audio devices available:', audioDevices);
                })
                .catch((err) => $log.error('Error enumerating devices:', err));

              // Start monitoring audio levels every 500ms
              audioMonitor = $interval(checkAudio, 500);

              // Array to store audio data chunks as they come in
              const chunks = [];

              /**
               * Event handler for when audio data becomes available
               * This is called periodically during recording (every 100ms as set in start())
               */
              recorder.ondataavailable = (e) => {
                $log.log('Data available event:', {
                  size: e.data.size,
                  type: e.data.type,
                  state: recorder.state,
                  timestamp: new Date().toISOString(),
                });

                if (e.data.size > 0) {
                  chunks.push(e.data);
                } else {
                  $log.warn('Received empty data chunk');
                }
              };

              /**
               * Event handler for recorder errors
               */
              recorder.onerror = (e) => {
                $log.error('Recorder error:', e);
                $interval.cancel(audioMonitor);
                deferred.reject('Recording error: ' + e.name);
              };

              // Resolve the promise with an object containing recorder control methods
              deferred.resolve({
                /**
                 * Starts the recording process
                 * Clears any previous chunks and begins capturing audio
                 */
                start: () => {
                  chunks.length = 0; // Clear previous recording data
                  try {
                    recorder.start(100); // Start recording, get data every 100ms
                    $log.log('Recording started, state:', recorder.state);
                  } catch (e) {
                    $log.error('Error starting recorder:', e);
                    $interval.cancel(audioMonitor);
                    throw e;
                  }
                },

                /**
                 * Stops the recording and processes the captured audio
                 * @returns {Promise} Promise that resolves with the audio blob or rejects with an error
                 */
                stop: () => {
                  const deferred = $q.defer();
                  let timeoutId;

                  // Request any final data that hasn't been delivered yet
                  recorder.requestData();

                  /**
                   * Event handler for when recording stops
                   * This processes all collected audio chunks into a single blob
                   */
                  recorder.onstop = () => {
                    $log.log('Recording stopped, state:', recorder.state);

                    // Give time for the last chunk to arrive before processing
                    timeoutId = $timeout(() => {
                      $log.log('Final chunks:', chunks.length);

                      if (chunks.length === 0) {
                        deferred.reject('No audio data captured');
                        return;
                      }

                      try {
                        // Create a single blob from all audio chunks
                        const blob = new Blob(chunks, {
                          type: supportedType,
                        });
                        $log.log('Created blob:', {
                          size: blob.size,
                          type: blob.type,
                        });

                        if (blob.size === 0) {
                          deferred.reject('Created empty audio blob');
                          return;
                        }

                        // Stop all audio tracks to release the microphone
                        stream.getTracks().forEach((track) => {
                          $log.log(
                            'Stopping track:',
                            track.label,
                            track.readyState,
                          );
                          track.stop();
                        });
                        deferred.resolve(blob);
                      } catch (e) {
                        $log.error('Error creating blob:', e);
                        deferred.reject(e);
                      }
                    }, 100); // Wait 100ms for final chunks
                  };

                  try {
                    recorder.stop(); // Stop the recorder
                  } catch (e) {
                    $log.error('Error stopping recorder:', e);
                    $interval.cancel(audioMonitor);
                    $timeout.cancel(timeoutId);
                    deferred.reject(e);
                  }

                  return deferred.promise;
                },

                /**
                 * Checks if recording is currently in progress
                 * @returns {boolean} True if recording, false otherwise
                 */
                isRecording: () => recorder.state === 'recording',

                /**
                 * Cleans up all resources used by the recorder
                 * Should be called when done with the recorder to prevent memory leaks
                 */
                cleanup: () => {
                  $interval.cancel(audioMonitor);
                  if (recorder.state === 'recording') {
                    recorder.stop();
                  }
                  stream.getTracks().forEach((track) => track.stop());
                  audioContext.close();
                },
              });
            })
            .catch((err) => {
              $interval.cancel(audioMonitor);
              deferred.reject(err);
            });

          return deferred.promise;
        },
      };
    },
  )
  .component('voiceInput', {
    template: voiceInputTemplate,
    transclude: true,
    bindings: {
      enabled: '<',
      disabled: '<',
      onTextChange: '&',
    },
    controller: 'VoiceInputCtrl',
  })
  .controller(
    'VoiceInputCtrl',
    /* @ngInject */
    function VoiceInputController(
      $scope,
      $window,
      $log,
      $timeout,
      $http,
      RecorderService,
    ) {
      const ctrl = this;

      ctrl.isRecording = false;
      ctrl.error = null;
      ctrl.isSupported = !!($window.MediaRecorder && navigator.mediaDevices);

      let recorder = null;
      let inputElement = null;

      /**
       * Starts the audio recording process
       * Initializes the recorder if needed and begins capturing audio
       */
      function startRecording() {
        if (ctrl.isRecording || ctrl.disabled) return;

        inputElement = document.activeElement;
        ctrl.error = null;

        if (!recorder) {
          // First time recording - create a new recorder
          RecorderService.createRecorder()
            .then((rec) => {
              recorder = rec;
              ctrl.isRecording = true;
              recorder.start();
            })
            .catch((error) => {
              ctrl.error = 'Error initializing recorder: ' + error.message;
              $log.error('Recorder initialization error:', error);
            });
        } else {
          // Recorder already exists, just start it
          ctrl.isRecording = true;
          recorder.start();
        }
      }

      /**
       * Stops the audio recording and processes the result
       * Sends the audio to the server for transcription
       */
      function stopRecording() {
        if (!recorder || !ctrl.isRecording) return;

        ctrl.isRecording = false;
        recorder
          .stop()
          .then((audioBlob) => {
            $log.log('Audio blob:', {
              type: audioBlob.type,
              size: audioBlob.size,
            });

            if (audioBlob.size === 0) {
              throw new Error('No audio data captured');
            }

            // Prepare the audio data for upload
            const formData = new FormData();
            formData.append('audio', audioBlob, 'recording.webm');

            // Send the audio to the server for transcription
            return $http
              .post(appConfig.audioRecordingUploadRoute, formData, {
                headers: {
                  'Content-Type': undefined,
                },
                transformRequest: angular.identity,
              })
              .catch((error) => {
                $log.error('Server error:', error.data);
                throw error.data.error || error.statusText || 'Server error';
              });
          })
          .then((response) => {
            if (response.data && response.data.text) {
              // Successfully got transcription, pass it to the callback
              ctrl.onTextChange({ text: response.data.text });
            } else {
              throw new Error('No transcription received');
            }
          })
          .catch((error) => {
            ctrl.error =
              typeof error === 'string'
                ? error
                : 'Error processing audio. Please try again.';
            $log.error('Speech-to-text error:', error);
          })
          .finally(() => {
            // Reset recorder so a new one will be created next time
            if (recorder) {
              recorder.cleanup();
              recorder = null;
            }

            // Restore focus to the previous element
            if (inputElement) {
              $timeout(() => inputElement.focus(), 0);
            }
          });
      }

      /**
       * Toggles recording state between start and stop
       */
      ctrl.toggleRecording = function () {
        if (ctrl.isRecording) {
          stopRecording();
        } else {
          startRecording();
        }
      };

      /**
       * Handles changes to component bindings
       * Stops recording if component becomes disabled
       */
      ctrl.$onChanges = function (changes) {
        if (
          changes.disabled &&
          changes.disabled.currentValue &&
          ctrl.isRecording
        ) {
          stopRecording();
        }
      };

      /**
       * Cleanup when component is destroyed
       * Ensures all resources are properly released
       */
      ctrl.$onDestroy = function () {
        if (recorder) {
          recorder.cleanup();
          recorder = null;
        }
        inputElement = null;
      };
    },
  );
